import styled, { css } from 'styled-components';
import { up } from 'styled-breakpoints';

import EquityIcon from '../../../../assets/icons/equity-funds.svg';
import DebtIcon from '../../../../assets/icons/debt-funds.svg';
import HybridIcon from '../../../../assets/icons/hybrid-funds.svg';
import LiquidIcon from '../../../../assets/icons/liquid-funds.svg';
import OtherIcon from '../../../../assets/icons/other-funds.svg';
import NfoIcon from '../../../../assets/icons/nfo.svg';

import BlueChipIcon from '../../../../assets/icons/blue-chip.svg';
import DiversifyAssetIcon from '../../../../assets/icons/diversify-asset.svg';
import RetirementFundsIcon from '../../../../assets/icons/gold-funds.svg';
import IndexFundsIcon from '../../../../assets/icons/index-funds.svg';
import InternationalFundsIcon from '../../../../assets/icons/international-funds.svg';
import LargeMidcapIcon from '../../../../assets/icons/midcap.svg';
import {
  EXPLORE_SCHEMES_USER_FLOW,
  OTHER_FUND,
  returnDuration,
  returnDurationSaveTax,
  SAVE_TAX_SUB_CAT,
} from '../../../../constants/ExplorePage';
import { FilterCat, FundFilters, FundFiltersData } from '../../../../pages/explore/[fundType]';
import { pxToRem } from '../../../../styles/utils';
import { PLAN_SIP_FUND_TYPE } from '../../../../constants/common';
import { CategoryChooseGoal, ChooseGoals, DurationChooseGoal } from '../../../../pages/planSIP';

export const getIconForFundCard = (categoryCode: string, subCategory?: string | undefined) => {
  if (categoryCode && !subCategory) {
    // For Explore Page icons via Different funds from different needs journey
    switch (categoryCode) {
      case 'Equity':
        return <EquityIcon />;
      case 'Debt':
        return <DebtIcon />;
      case 'Hybrid':
        return <HybridIcon />;
      case 'Liquid':
        return <LiquidIcon />;
      case 'Other':
        return <OtherIcon />;
      case 'NFO' :
        return <NfoIcon />;
      default:
        return <EquityIcon />;
    }
  } else {
    // For Investment Ideas page icons
    switch (subCategory) {
      case 'ELF':
        return <StyledBlueChipIcon />;
      case 'EFC,FLC':
        return <StyledLargeMidcapIcon />;
      case 'EIF':
        return <StyledIndexFundsIcon />;
      case 'HMA':
        return <StyledDiversifyAssetIcon />;
      case 'INF,OFO':
        return <StyledInternationalFundsIcon />;
      case 'SRF':
        return <StyledRetirementFundsIcon />;
      default:
        return <EquityIcon />;
    }
  }
};

export const getBackButtonTitleSchemeListPage = (
  subCategory: string,
  fundType: string,
  goalKey?: string | undefined,
  userFlow?: string
) => {
  if (subCategory && userFlow && userFlow === EXPLORE_SCHEMES_USER_FLOW.INVESTMENT_IDEAS) {
    switch (subCategory) {
      case SAVE_TAX_SUB_CAT:
        return 'Save Tax (ELSS)';
      // Below cases are applicable only for Investment Ideas page
      case 'ELF':
        return 'Bluechip Funds';
      case 'EFC':
        return 'Large, Mid & Small Cap Funds';
      case 'EIF':
        return 'Index Funds';
      case 'HMA':
        return 'Diversify Across Asset Classes';
      case 'INF,OFO':
        return 'International Funds';
      case 'SRF':
        return 'Retirement Funds';
      // Below cases are applicable when we route to scheme details page from the nudges in transact success pages
      case 'ESF':
        return 'Smallcap funds';
      case 'EMF':
        return 'Midcap funds';
      case 'BSF':
        return 'Banking and PSU';
      case 'HDB':
        return 'Dynamic Asset Allocation Funds';
      default:
        return 'Back';
    }
  } else if (subCategory) {
    switch (subCategory) {
      case SAVE_TAX_SUB_CAT:
        return 'Save Tax (ELSS)';
      case 'ELF':
        return 'Large Cap Funds';
      case 'EFC':
        return 'Multi Cap Funds';
      case 'EIF':
        return 'Index Funds';
      case 'HMA':
        return 'Diversify Across Asset Classes';
      case 'INF,OFO':
        return 'International Funds';
      case 'SRF':
        return 'Retirement Funds';
      // Below cases are applicable when we route to scheme details page from the nudges in transact success pages
      case 'ESF':
        return 'Smallcap funds';
      case 'EMF':
        return 'Midcap funds';
      case 'BSF':
        return 'Banking and PSU';
      case 'HDB':
        return 'Dynamic Asset Allocation Funds';
      default:
        return 'Back';
    }
  } else {
    if (fundType === PLAN_SIP_FUND_TYPE) {
      switch (goalKey) {
        case 'EmergencySavings':
          return 'Emergency Savings';
        case 'ShortTerm':
          return 'Short Term Goals';
        case 'MediumTerm':
          return 'Medium Term Goals';
        case 'LongTerm':
          return 'Long Term Goals';
        case 'TaxSaving':
          return 'Tax Saving';
      }
    } else if(fundType === 'NFO'){
      return 'NFOs'
    } else 
        return fundType + ' Funds';
  }
};

export const getDurationSchemeListPage = (subCategory: string | string[], duration: Array<FilterCat>) => {
  if (subCategory) {
    if (subCategory === SAVE_TAX_SUB_CAT) {
      return returnDurationSaveTax;
    }
    return returnDuration;
  } else {
    return duration;
  }
};

const investmentIdeasIconStyling = css`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  ${up('md')} {
    width: ${pxToRem(48)};
    height: ${pxToRem(48)};
  }
`;
const StyledBlueChipIcon = styled(BlueChipIcon)`
  ${investmentIdeasIconStyling};
`;

const StyledDiversifyAssetIcon = styled(DiversifyAssetIcon)`
  ${investmentIdeasIconStyling};
`;
const StyledRetirementFundsIcon = styled(RetirementFundsIcon)`
  ${investmentIdeasIconStyling};
`;
const StyledIndexFundsIcon = styled(IndexFundsIcon)`
  ${investmentIdeasIconStyling};
`;
const StyledInternationalFundsIcon = styled(InternationalFundsIcon)`
  ${investmentIdeasIconStyling};
`;
const StyledLargeMidcapIcon = styled(LargeMidcapIcon)`
  ${investmentIdeasIconStyling};
`;

export const renameFundDataFieldsWithSimilarToInvIdeasField = (fundData: Array<FundFilters>) => {
  if (fundData && fundData.length > 0) {
    // This util changes the name of the key from fundCategoryTitle, fundCategoryDescription, and fundCategoryName
    // to categoryName, categoryDescription, categoryCode, respectively.
    const newFundData = fundData.map(
      ({
        fundCategoryTitle: categoryName,
        fundCategoryDescription: categoryDescription,
        fundCategoryName: categoryCode,
        ...rest
      }) => ({
        categoryName,
        categoryDescription,
        categoryCode,
        ...rest,
      })
    );
    return newFundData;
  }
};

// Renaming the data of array categories
const mapPlanSipTosubCat = (categories: Array<CategoryChooseGoal>) => {
  const subCat = categories.map((cat) => {
    return {
      key: cat.subCategoryCode,
      displayName: cat.subCategoryName,
      displayText: cat.subCategoryDescription,
      catCode: cat.categoryCode,
    };
  });
  return subCat;
};

// Renaming duration array's key names from durationCode, durationName to displayName and key respectively
const mapPlanSipToDuration = (duration: Array<DurationChooseGoal>) => {
  const durations = duration.map((dur) => {
    return {
      displayName: dur.durationCode,
      key: dur.durationName,
    };
  });

  return durations;
};

// Formatting or converting the data of plan your goals SIP format to
// explore funds schemes format to reuse the same component for both the journeys.
export const getFilterDataFromGoalsData = (fundFiltersData: ChooseGoals) => {
  const newFundData = {
    data: {
      data: [
        {
          subCategories: mapPlanSipTosubCat(fundFiltersData.categories),
          durations: mapPlanSipToDuration(fundFiltersData.duration),
          defaultDuration: fundFiltersData.defaultDuration,
          defaultSubCateogory: fundFiltersData.defaultSubCategory,
        },
      ],
    },
  };
  return newFundData;
};

export const getCategory = (subCategory: string, fundType: string, fundCategoryKey?: string, catCode?: string) => {
  if (subCategory) {
    // For Save Tax and Investment Ideas journey
    return fundType;
  } else if (fundCategoryKey) {
    // For Diff Funds for Diff needs journey
    return fundCategoryKey;
  } else {
    // Plan your goals with SIP journey
    return catCode;
  }
};

export const getSubCategory = (subCategory: string, subCategoryKey: string) => {
  if (subCategory) {
    // For Save Tax and Investment Ideas journey
    return subCategory;
  } else {
    // For Diff Funds for Diff needs and Plan your goals with SIP journey
    return subCategoryKey;
  }
};

export const getNfoCategory = (nfoCategoryKey:string) => {
  if (nfoCategoryKey) {
    // For Save Tax and Investment Ideas journey
    return nfoCategoryKey;
  } 
};

export const getFundsNameForGTAEvent = (userFlow: string, categoryCode: string, categoryName: string) => {
  if(userFlow === EXPLORE_SCHEMES_USER_FLOW.EXPLORE) return categoryName;
  if (categoryCode === OTHER_FUND) {
    return categoryCode;
  } else if (userFlow === EXPLORE_SCHEMES_USER_FLOW.INVESTMENT_IDEAS) {
    return `${categoryName}`;
  } else {
    // For Diff funds for diff need flow
    return categoryCode + ' Funds';
  }
};

// To find out the tooltip data using category subcategory. Only for journey via banner
// Ex - category = EQ,OTH, subCategory = INF,OFO
export const getDataForTooltip = (fundListData: FundFiltersData, category: string, subCategory: string) => {
  const categories = category.split(',');
  // categories will now be ['EQ', 'OTH']
  const subCategories = subCategory.split(',');
  // subCategories will now be ['INF', 'OFO']
  const fundData = fundListData?.data?.data;
  const tooltipSet = new Set();
  // Extract the data for all the category in "categories" array one by one using map
  categories &&
    !!categories.length &&
    categories.map((cat) => {
      // For first iteration, data related to EQ category will be returned
      const filteredData = fundData.filter((filter) => filter.fundCategoryKey.includes(cat));
      // Now iterate through all subCat of "subCategories" array
      subCategories &&
        !!subCategories.length &&
        subCategories.map((subCat) => {
          // Filter out the data when a particular subCat matches
          const subCatfilteredData =
            filteredData &&
            !!filteredData.length &&
            filteredData[0].subCategories.filter((filter) => filter.key.includes(subCat));
          // subCatfilteredData will be array of only 0 or 1 element with data key, displayName, and displayText
          if (!!subCatfilteredData.length) {
            // Stringified so that same "subCatfilteredData" is not added to the data(To handle case where subCategory is comma seprated value like "OTH,SOS")
            tooltipSet.add(JSON.stringify(subCatfilteredData));
          }
        });
    });
  return tooltipSet;
};

export const getDisplayText = (setData) => {
  let displayText = '';
  if (setData.size > 0) {
    setData.forEach((value: string) => {
      // Extract the object from the string which we stringified in function "getDataForTooltip"
      const parsedSet = JSON.parse(value);
      displayText = displayText + '\n' + parsedSet[0].displayText;
    });
  }
  return displayText;
};
